import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  inject,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { selectUrl } from '@app/_store/selectors/router.selectors';
import { AppDataRefreshService } from '@core/services/app-data-refresh.service';
import { ScrollResetService } from '@core/services/scroll-reset.service';
import { MetaPixelService } from '@core/services/tracking/meta-pixel.service';
import { MixpanelService } from '@core/services/tracking/mixpanel.service';
import { Store } from '@ngrx/store';
import { catchError, concatMap, delay, filter, of, take, tap } from 'rxjs';

@Component({
  host: {
    '(window:resize)': 'onResize()',
  },
  selector: 'app-root',
  template: `<router-outlet></router-outlet>`,
  styles: `
    :host {
      display: flex;
      flex: 1 1 auto;
      width: 100%;
      height: 100%;
    }
  `,
  imports: [RouterOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly mixpanel = inject(MixpanelService);
  private readonly metaPixel = inject(MetaPixelService);
  private readonly refreshService = inject(AppDataRefreshService);
  private readonly scrollReset = inject(ScrollResetService);

  private readonly currentUrl = this.store.selectSignal(selectUrl);

  constructor() {
    this.refreshService.init();
    this.scrollReset.init(() => document.querySelector('.layout-content'));

    afterNextRender({ write: () => this.setVhUnit() });
    afterNextRender(() => this.trackAppOpen());
  }

  protected onResize(): void {
    this.setVhUnit();
  }

  /** Fires once, on the first completed navigation that isn't the auth callback. */
  private trackAppOpen(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        delay(300),
        take(1),
        tap(() => this.mixpanel.track('Web app - open zone visit')),
        concatMap(() => this.metaPixel.setupCookies()),
        concatMap(() => this.metaPixel.trackEvent('OpenWebApp')),
        catchError((error) => {
          console.error('MetaPixel flow error', error);

          return of(null);
        })
      )
      .subscribe();
  }

  private setVhUnit(): void {
    document.documentElement.style.setProperty(
      '--vh',
      `${window.innerHeight * 0.01}px`
    );
  }
}
